import { useState } from "react";
import {
  PaymentElement,
  useStripe,
  useElements
} from "@stripe/react-stripe-js";

import { Loader, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

export default function StripePaymentModal({
  isOpen,
  onClose,
  onPaymentConfirm,
  paymentIntent,
  loading = false,
  customerName,
  customerEmail,
  customerPhone,
  restaurant
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [processing, setProcessing] = useState(false);
const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const BACKEND_URL = window.location.origin;
  const API = `${BACKEND_URL}/api`;

  // ---------------------------------------------------
  // Don't Render
  // ---------------------------------------------------

  if (!isOpen || !paymentIntent) {
    return null;
  }

  // ---------------------------------------------------
  // Handle Payment
  // ---------------------------------------------------

const handleSubmit = async (e) => {

  e.preventDefault();

  if (!stripe || !elements) {

    toast.error("Stripe not initialized");

    return;
  }

  setProcessing(true);

  try {

    const { error } = await stripe.confirmPayment({

      elements,

      confirmParams: {

        return_url:
          `${window.location.origin}/payment-success`,

        payment_method_data: {

          billing_details: {

            name: customerName || "",

            email: customerEmail || "",

            phone: customerPhone || ""
          }
        }
      },

      redirect: "if_required"
    });

    if (error) {

      console.error(error);

      toast.error(
        error.message || "Payment failed"
      );

      return;
    }

    /**
     * IMPORTANT
     *
     * For card payments:
     * Stripe may NOT redirect.
     *
     * So manually redirect.
     */

const retrievedIntent =
  await stripe.retrievePaymentIntent(
    paymentIntent.client_secret
  );

if (
  retrievedIntent?.paymentIntent?.status ===
  "succeeded"
) {
  setPaymentSucceeded(true);
//  await new Promise((resolve) =>
//     setTimeout(resolve, 1000)
//   );
  const paymentIntentId =
    retrievedIntent.paymentIntent.id;

  let order = null;

  // -----------------------------------------
  // WAIT FOR WEBHOOK TO CREATE ORDER
  // -----------------------------------------

  for (let i = 0; i < 20; i++) {

    try {

      const response = await axios.get(
        `${API}/payments/order-by-intent/${paymentIntentId}`,
        {
          params: {
            restaurant_id: restaurant.id
          }
        }
      );

      order = response.data?.order;

  if (
    order &&
    (
      order.delivery_type === "pickup" ||
      order.uber_tracking_url
    )
  ) {
    break;
  }

    } catch (error) {

      // ignore 404 while waiting
    }

    // wait 1 second
    await new Promise((resolve) =>
      setTimeout(resolve, 1000)
    );
  }

  if (!order) {

    toast.error(
      "Order creation taking longer than expected"
    );

    return;
  }

  // -----------------------------------------
  // REDIRECT
  // -----------------------------------------

  if (
    order.uber_tracking_url &&
    order.delivery_type !== "pickup"
  ) {

    window.location.href =
      order.uber_tracking_url;

  } else {

    window.location.href =
      `/track/${restaurant.subdomain}?order_id=${order.id}`;
  }
}

  } catch (error) {

    console.error(error);

    toast.error(
      error?.response?.data?.detail ||
      "Payment processing failed"
    );

  } finally {

    setProcessing(false);
  }
};

  // ---------------------------------------------------
  // UI
  // ---------------------------------------------------
if (paymentSucceeded) {
  return (
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl p-6 sm:p-8 text-center">

        {/* Success Icon */}
        <div className="flex justify-center mb-5">
          <div className="h-20 w-20 rounded-full bg-green-100 flex items-center justify-center">
            <svg
              className="h-10 w-10 text-green-600"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
          Order Placed Successfully!
        </h2>

        {/* Description */}
        <p className="mt-3 text-gray-600 text-sm sm:text-base leading-relaxed">
          We've received your order and it will be ready soon.
        </p>

        {/* Status Card */}
        <div className="mt-6 bg-green-50 border border-green-200 rounded-2xl p-4">
          <div className="flex items-center justify-center gap-2">
            <Loader
              size={18}
              className="animate-spin text-green-600"
            />
            <span className="font-medium text-green-700">
              Preparing your order details...
            </span>
          </div>

          <p className="mt-2 text-xs sm:text-sm text-green-600">
            Please wait while we redirect you to your order tracking page.
          </p>
        </div>

        {/* Footer */}
        <div className="mt-6">
          <p className="text-xs text-gray-400">
            Do not close this window.
          </p>
        </div>

      </div>
    </div>
  );
}
return (
  
  <div className="fixed inset-0 z-[150] overflow-y-auto bg-black/50 p-3">
    
    <div className="flex min-h-full items-start justify-center sm:items-center">
      
      <div className="w-full max-w-md rounded-2xl bg-white p-5 shadow-2xl max-h-[95vh] overflow-y-auto">

        {/* Header */}

        <div className="mb-5 flex items-center justify-between">
          
          <h2 className="text-2xl font-bold text-gray-900">
            Secure Payment
          </h2>

          <button
            type="button"
            onClick={onClose}
            disabled={    processing ||  paymentSucceeded}
            className="rounded-lg p-2 transition hover:bg-gray-100 disabled:opacity-50"
          >
            <X size={22} />
          </button>

        </div>

        {/* Form */}

        <form
          onSubmit={handleSubmit}
          className="space-y-5"
        >

          {/* Amount */}

          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
            <p className="text-sm text-gray-600">
              Amount to Pay
            </p>

            <p className="mt-1 text-3xl font-bold text-blue-700">
              {paymentIntent.currency_symbol}
              {paymentIntent.grand_total.toFixed(2)}
            </p>
          </div>

          {/* Customer */}

          <div className="rounded-xl bg-gray-50 p-4 text-sm">
            
            <div className="mb-2">
              <span className="font-semibold text-gray-700">
                Name:
              </span>{" "}
              <span className="text-gray-600">
                {customerName}
              </span>
            </div>

            <div className="mb-2">
              <span className="font-semibold text-gray-700">
                Email:
              </span>{" "}
              <span className="text-gray-600">
                {customerEmail || "N/A"}
              </span>
            </div>

            <div>
              <span className="font-semibold text-gray-700">
                Phone:
              </span>{" "}
              <span className="text-gray-600">
                {customerPhone}
              </span>
            </div>

          </div>

          {/* Stripe Payment Element */}

          <div className="rounded-xl border border-gray-300 bg-white p-4">
            <PaymentElement />
          </div>

          {/* Pay Button */}

          <button
            type="submit"
            disabled={
              !stripe ||
              !elements ||
              processing ||
              loading||
              paymentSucceeded
            }
            className="flex w-full items-center justify-center gap-2 rounded-xl bg-red-600 py-3 font-bold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-400"
          >
            {processing || loading ? (
              <>
                <Loader
                  size={20}
                  className="animate-spin"
                />
                Processing...
              </>
            ) : (
              <>
                Pay{" "}
                {paymentIntent.currency_symbol}
                {paymentIntent.grand_total.toFixed(2)}
              </>
            )}
          </button>

          {/* Cancel */}

          <button
            type="button"
            onClick={onClose}
            disabled={processing || loading || paymentSucceeded}
            className="w-full rounded-xl border border-gray-300 py-3 font-semibold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:bg-gray-100"
          >
            Cancel
          </button>

          {/* Footer */}

          <p className="text-center text-xs text-gray-500">
            🔒 Secured by Stripe
          </p>

        </form>

      </div>
    </div>
  </div>
  
);
}